import { z } from "zod";
/*
 * Workspace file reads a running turn makes through the tenant API: the file's record, its bytes
 * and the extraction receipts kept for it. Each request carries the run's lease; the tenant answers
 * only while that attempt holds the run, and only for a file the run's actor may read under the
 * same rule as a download, its own session's attachments and its attached channel.
 */
const id = z.string().trim().min(1).max(200);
const lease = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
};
const fileRef = { ...lease, fileId: id };
export const workspaceFileExtractionProviderSchema = z.enum(["plaintext", "spreadsheet", "reducto", "openai", "mistral"]);
const sourcePayloadHash = z.string().trim().min(1).max(200);
const extractionKey = { ...fileRef, provider: workspaceFileExtractionProviderSchema, sourcePayloadHash };
const reason = z.string().trim().min(1).max(10_000).optional();
const fileSizeBytes = z.number().int().nonnegative();
const nullableJson = z.record(z.string(), z.unknown()).nullable();
export const workerFileRequestSchema = z.discriminatedUnion("operation", [
    z.object({ ...fileRef, operation: z.literal("open") }).strict(),
    z.object({ ...extractionKey, operation: z.literal("extraction.read") }).strict(),
    z.object({ ...extractionKey, operation: z.literal("extraction.submitted"), fileSizeBytes, reason }).strict(),
    z.object({
        ...extractionKey, operation: z.literal("extraction.completed"),
        model: z.string().nullable(), providerJobId: z.string().nullable(), latencyMs: z.number().int().nonnegative(),
        result: z.record(z.string(), z.unknown()), extractedText: z.string(), usage: nullableJson,
        numPages: z.number().int().nonnegative().nullable(), credits: z.number().nonnegative().nullable(),
    }).strict(),
    z.object({
        ...extractionKey, operation: z.literal("extraction.skipped"), fileSizeBytes, reason,
        model: z.string().nullable(), errorCode: z.string().trim().min(1).max(200), errorMessage: z.string(),
    }).strict(),
    z.object({
        ...extractionKey, operation: z.literal("extraction.failed"),
        latencyMs: z.number().int().nonnegative(), errorMessage: z.string(),
    }).strict(),
    z.object({ ...fileRef, operation: z.literal("structured.read"), extractionId: id }).strict(),
    z.object({
        ...fileRef, operation: z.literal("structured.retain"), extractionId: id, sourcePayloadHash,
        schemaHash: z.string().regex(/^[a-f0-9]{64}$/u), parseJobId: id,
        schema: z.record(z.string(), z.unknown()), evidence: z.record(z.string(), z.unknown()),
    }).strict(),
]);
export const workerFileSchema = z.object({
    id, organisationId: id,
    /** The first channel, session and record the file is linked to, if any. */
    channelId: id.nullable(), sessionId: id.nullable(), recordId: id.nullable(),
    fileName: z.string(), contentType: z.string(), byteSize: z.number().int().nonnegative(),
    storageProvider: z.string(), checksumSha256: z.string().nullable(),
    createdAt: z.number().int(), updatedAt: z.number().int(),
}).strict();
export const workerFileExtractionSchema = z.object({
    provider: workspaceFileExtractionProviderSchema,
    status: z.enum(["submitted", "completed", "skipped", "failed"]),
    model: z.string().nullable(), providerJobId: z.string().nullable(), latencyMs: z.number().int().nullable(),
    extractedText: z.string().nullable(), usage: nullableJson,
    numPages: z.number().int().nullable(), credits: z.number().nullable(),
    errorCode: z.string().nullable(), errorMessage: z.string().nullable(),
}).strict();
export const workerFileResponseSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("open"), file: workerFileSchema }).strict(),
    z.object({ operation: z.literal("extraction.read"), extraction: workerFileExtractionSchema.nullable() }).strict(),
    z.object({ operation: z.literal("extraction.submitted"), extraction: workerFileExtractionSchema }).strict(),
    z.object({ operation: z.literal("extraction.completed"), extraction: workerFileExtractionSchema }).strict(),
    z.object({ operation: z.literal("extraction.skipped"), extraction: workerFileExtractionSchema }).strict(),
    z.object({ operation: z.literal("extraction.failed"), extraction: workerFileExtractionSchema }).strict(),
    z.object({ operation: z.literal("structured.read"), evidence: z.record(z.string(), z.unknown()).nullable() }).strict(),
    z.object({ operation: z.literal("structured.retain"), evidence: z.record(z.string(), z.unknown()) }).strict(),
]);
/** The bytes of one file, read with the run's lease from `/api/worker/files/content`. */
export const workerFileContentRequestSchema = z.object(fileRef).strict();
/** Stable refusal codes; the executor turns each into the tool error or retry the agent sees. */
export const WORKER_FILE_REFUSALS = {
    leaseNotHeld: "file_lease_not_held",
    fileNotFound: "file_not_found",
    storageUnsupported: "file_storage_unsupported",
    contentMissing: "file_content_missing",
    extractionMissing: "file_extraction_missing",
    identityMismatch: "structured_extraction_identity_mismatch",
};
